<script lang="ts" setup>
import { computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';
import { type NavigationItem } from '../../types';
import NavigationContent from '../tableOfContent/NavigationContent.vue';
import NavigationCard from '../tableOfContent/NavigationCard.vue';

type Props = {
  navigationItems?: NavigationItem[];
};

defineProps<Props>();

const { smAndDown: isMobile, width: screenWidth } = useDisplay();

const uiStore = useUiStore();
const { isTableOfContentVisible, bottomBarHeight, topBarHeight } = storeToRefs(uiStore);
const { toggleTableOfContentDrawer } = uiStore;

const { isTocBottom, isAudioOnly } = storeToRefs(useReaderStore());

const navigationDrawer = ref<HTMLElement | null>(null);

// Mounted on first open and kept, so closing does not blank the drawer mid-transition. A book's
// lists can hold thousands of entries; mounting them with the reader stalls the WebView on load.
const hasBeenOpened = ref(isTableOfContentVisible.value);

const drawerWidth = computed(() => (isMobile.value ? screenWidth.value : 420));

const drawerClasses = computed(() => {
  const barState = isTocBottom.value ? 'no-bar' : 'with-bar';

  return [
    'navigation-drawer-custom',
    {
      'audio-only-mode': isAudioOnly.value,
      'toc-at-bottom': isTocBottom.value,
      'toc-at-top': !isTocBottom.value,
    },
    `height-mode-${barState}`,
  ];
});

// @ts-ignore - this is the recommended way to import it - https://www.npmjs.com/package/@vueuse/integrations
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
const { activate, deactivate } = useFocusTrap(navigationDrawer, { allowOutsideClick: true });

const dynamicTopBarHeight = computed(() => `${topBarHeight.value}px`);
const dynamicBottomBarHeight = computed(() => `${bottomBarHeight.value}px`);

watch(isTableOfContentVisible, async (visible) => {
  if (visible) {
    hasBeenOpened.value = true;
    await nextTick();
    activate();
  } else {
    deactivate();
  }
});
</script>

<template>
  <v-bottom-sheet
    v-if="isMobile && isAudioOnly"
    data-testid="bp-navigation-sheet"
    :model-value="isTableOfContentVisible"
    :inert="!isTableOfContentVisible"
    :class="drawerClasses"
    @update:model-value="toggleTableOfContentDrawer"
  >
    <div ref="navigationDrawer" class="focus-trap-wrapper">
      <NavigationCard @close="toggleTableOfContentDrawer(false)">
        <NavigationContent :navigation-items="navigationItems">
          <template #default="slotProps">
            <slot v-if="hasBeenOpened" v-bind="slotProps" />
          </template>
        </NavigationContent>
      </NavigationCard>
    </div>
  </v-bottom-sheet>
  <v-navigation-drawer
    v-else
    data-testid="bp-navigation-drawer"
    :model-value="isTableOfContentVisible"
    :inert="!isTableOfContentVisible"
    :width="drawerWidth"
    :class="drawerClasses"
    floating
    temporary
    touchless
    @update:model-value="toggleTableOfContentDrawer"
  >
    <div ref="navigationDrawer">
      <NavigationContent
        :navigation-items="navigationItems"
        @close-table-of-content="toggleTableOfContentDrawer(false)"
      >
        <template #default="slotProps">
          <slot v-if="hasBeenOpened" v-bind="slotProps" />
        </template>
      </NavigationContent>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.navigation-drawer-custom {
  --bar-top-height: v-bind('dynamicTopBarHeight');
  --bar-bottom-height: calc(v-bind('dynamicBottomBarHeight') + env(safe-area-inset-bottom, 0px));

  padding-left: 16px;
  padding-right: 16px;

  &.toc-at-bottom {
    top: 0 !important;
  }

  &:not(.audio-only-mode) {
    &.toc-at-top {
      top: calc(var(--bar-top-height) + env(safe-area-inset-top, 0px)) !important;
    }

    &.height-mode-with-bar {
      height: calc(100dvh - var(--bar-top-height) - var(--bar-bottom-height)) !important;
    }
    &.height-mode-no-bar {
      height: calc(100dvh - var(--bar-bottom-height)) !important;
    }
  }
}

.focus-trap-wrapper {
  border-radius: 10px 10px 0 0 !important;
  overflow: auto !important;
}
</style>
